import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@workos-inc/authkit-react";
import {
  ChevronDown,
  ChevronUp,
  LifeBuoy,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Sun,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SidebarFooter } from "@/components/ui/sidebar";
import { useTheme } from "@/contexts/theme-context";
import { useAuthUserDisplay } from "@/hooks/use-auth-user-display";
import { useCurrentUser } from "@/hooks/use-current-user";

const THEME_META = {
  system: { icon: Monitor, label: "System" },
  light: { icon: Sun, label: "Light Mode" },
  dark: { icon: Moon, label: "Dark Mode" },
} as const;

const CYCLE_ORDER = ["system", "light", "dark"] as const;

/**
 * SidebarUserProfile - Profile panel at the bottom of the home sidebar.
 *
 * Shows user avatar + name in collapsed state.
 * Expands to show Preferences, Help & Support, Theme toggle, and Sign Out.
 *
 * The name here is the one features label authored content with — comments, clarification
 * threads, assignment rows — because `useAuthUserDisplay` reads the Convex `users` row wherever
 * it exists and the AuthKit session only before it loads. `data-current-user-name` and
 * `data-current-user-directory-name` therefore report the same value: one identity, one source.
 */
export function SidebarUserProfile() {
  const { signOut } = useAuth();
  const currentUser = useCurrentUser();
  const { displayName, initials, email } = useAuthUserDisplay();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!expanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [expanded]);

  const currentThemeMeta = THEME_META[theme];
  const ThemeIcon = currentThemeMeta.icon;
  const nextTheme = CYCLE_ORDER[(CYCLE_ORDER.indexOf(theme) + 1) % CYCLE_ORDER.length]!;

  return (
    <SidebarFooter className="gap-0 p-0">
      <div className="h-px bg-border" />
      <div ref={containerRef} className="px-4 pb-4 pt-3">
        {/* Profile row */}
        <button
          type="button"
          data-testid="sidebar-user-profile-trigger"
          data-current-user-id={currentUser?._id}
          data-current-user-name={displayName}
          data-current-user-directory-name={displayName}
          className="flex w-full select-none items-center justify-between text-left"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-2">
            <div className="flex size-6 shrink-0 items-center justify-center bg-muted text-[10px] font-semibold text-muted-foreground">
              {initials}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-[13px] font-medium text-foreground truncate">
                {displayName}
              </span>
              {email && <span className="text-xs text-neutral-dim truncate">{email}</span>}
            </div>
          </div>
          {expanded ? (
            <ChevronDown className="size-3 text-neutral-dim" />
          ) : (
            <ChevronUp className="size-3 text-neutral-dim" />
          )}
        </button>

        {/* Expanded menu items */}
        <div
          className="grid transition-[grid-template-rows] duration-150 ease-out"
          style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <div className="mt-2 space-y-0.5">
              <button
                type="button"
                data-testid="sidebar-user-profile-preferences"
                className="flex w-full select-none items-center gap-1.5 px-1 py-1 text-left text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                onClick={() => {
                  setExpanded(false);
                  navigate({ to: "/settings/profile" });
                }}
              >
                <Settings className="size-3 shrink-0" />
                <span>Preferences</span>
              </button>
              <button
                type="button"
                className="flex w-full select-none items-center gap-1.5 px-1 py-1 text-left text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                onClick={() => {
                  window.location.href = "mailto:support@latend.com";
                }}
              >
                <LifeBuoy className="size-3 shrink-0" />
                <span>Help & Support</span>
              </button>
              <button
                type="button"
                className="flex w-full select-none items-center gap-1.5 px-1 py-1 text-left text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                onClick={() => setTheme(nextTheme)}
              >
                <ThemeIcon className="size-3 shrink-0" />
                <span>{currentThemeMeta.label}</span>
              </button>
              <button
                type="button"
                className="flex w-full select-none items-center gap-1.5 px-1 py-1 text-left text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                onClick={() => signOut({ returnTo: window.location.origin })}
              >
                <LogOut className="size-3 shrink-0" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </SidebarFooter>
  );
}
